import style from './SolveTickets.module.css';
import React, { useState, useRef, FormEvent, useEffect, Suspense } from 'react';
import { fetchFromClient } from '../../../../fetch';
import { RequestDemoSpinner } from '@/components/NewRequestDemoForm/Spinner/RequestDemoSpinner';
import { PATH_DEMO } from '@/constants/spa-routes';
import { PatentSolution } from '@/components/PatentSolution/PatentSolution';
import { clsx } from 'clsx';
import { SwiperFreshdeskMain } from '@/components/PagesSections/FreshdeskIntegration/SolveTickets/SwiperFreshdeskMain';
import { AwardsComponent } from '@/components/AwardsComponent/AwardsComponent';
import { MediaRecognition } from '@/components/MediaRecognition/MediaRecognition';
import styled from 'styled-components';
import { Container } from '@/components/Containers/Container';
import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/constants/colors';
import { GreenButton } from '@/components/ButtonsCollection/GreenButton/GreenButton';

interface SolveTicketsProps {
  isMobile: boolean;
}

const initialValidation = {
  isValid: true,
  type: '', // incorrect, corporate
};

function getLeadSourceType() {
  const key = 'utm_medium';
  const params = new URLSearchParams(location.search);

  if (params.has(key)) {
    return params.get(key);
  }

  if (
    document.referrer.includes('https://www.google.com') ||
    document.referrer.includes('https://www.bing.com')
  ) {
    return 'organic';
  }

  if (sessionStorage.getItem(key)) {
    return sessionStorage.getItem(key);
  }

  return 'direct';
}

function getLeadSourceName() {
  const key = 'utm_source';
  const params = new URLSearchParams(location.search);

  if (params.has(key)) {
    return params.get(key);
  }

  if (document.referrer.includes('https://www.google.com')) {
    return 'googleSearch';
  }

  if (document.referrer.includes('https://www.bing.com')) {
    return 'bing';
  }

  if (sessionStorage.getItem(key)) {
    return sessionStorage.getItem(key);
  }

  return 'websiteVisit';
}

const ContentContainer = styled.div({
  height: '100%',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const SectionContainer = styled.section({
  minHeight: '100vh',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
});

export const SolveTickets = ({ isMobile }: SolveTicketsProps) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [validation, setValidation] = useState(initialValidation);
  const [requested, setRequested] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const validate = () => {
    const isEmailValid = /^([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/.test(email);

    if (!email) {
      return;
    }

    if (!isEmailValid) {
      setValidation({
        isValid: false,
        type: 'incorrect',
      });

      return;
    }
  };

  const submitForm = async (event: FormEvent) => {
    setSubmitting(true);
    event.preventDefault();
    const isEmailValid = /^([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/.test(email);

    if (!isEmailValid) {
      setValidation({
        isValid: false,
        type: 'incorrect',
      });

      setSubmitting(false);
      return;
    }

    let response = await fetchFromClient('GET', `/checkemail?email=${email}`);

    if (response instanceof Error) {
      setSubmitting(false);
      return console.warn(response.message);
    }

    const { isCorporateEmail } = response;

    if (!isCorporateEmail) {
      setSubmitting(false);
      setValidation({
        isValid: false,
        type: 'corporate',
      });
      return;
    }

    response = await fetchFromClient('POST', '/request-demo-with-email', {
      email,
      name: email,
      sourceUrl: window.location.href,
      leadSourceType: getLeadSourceType(),
      leadSourceName: getLeadSourceName(),
      tags: ['Freshdesk Page - Email'],
    });

    if (response.success) {
      window.dataLayer?.push({
        event: 'submit_form',
        status: 'successful',
        tag: 'Freshdesk Page - Email',
      });
    } else {
      setSubmitting(false);
      setValidation({
        isValid: !response.invalid,
        type: response.type,
      });
    }

    setSubmitting(false);
    setRequested(true);
  };

  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 1.5;
    }
  }, [videoRef]);

  return (
    <>
      <SectionContainer className="relative lg:bg-gradient_fresh bg-gradient_fresh_mobile w-full flex flex-col justify-between h-full bg-ebony">
        <video
          className="lg:min-h-[calc(100vh+150px)] absolute min-h-full bg-black w-full object-cover z-0 top-0 left-0 opacity-40"
          ref={videoRef}
          autoPlay
          muted
          playsInline
          loop
          disablePictureInPicture
          poster="/img/Video_2.0_frame.webp"
        >
          <source src={'/video/Video_cosupport_2.0.mp4'} type="video/mp4" />
        </video>
        <div className="w-full flex flex-col justify-between h-full md:min-h-screen items-center">
          <ContentContainer className="lg:mt-20-vw mt-24 lg:mb-14 mb-7.5 flex-1">
            <Container className={clsx('max-md:pb-10 relative')}>
              <div className="flex max-lg:flex-col justify-between lg:gap-16-vw gap-8 items-center w-full">
                <div className={clsx('w-full')}>
                  <Typography
                    $type="Heading1Large"
                    $color={COLORS.WHITE}
                    className="font-formula"
                    as="h1"
                  >
                    Turbocharge Support Responses with an AI Copilot for Freshdesk
                  </Typography>
                  <Typography
                    as="h4"
                    $type="Heading3"
                    className="font-jamjuree"
                    $color={COLORS.WHITE}
                  >
                    Improve your customer support with patented AI for Freshdesk. It handles support
                    requests independently, speeds up ticket response, and boosts agent efficiency
                    like never before.
                  </Typography>
                  <div
                    className={
                      requested ? `${style.successMessage} ${style.visible}` : style.successMessage
                    }
                  >
                    {isMobile ? (
                      <p>
                        Great, you're set! Thanks for opting to explore our demo – look out for our
                        follow-up. For a tailored experience, please{' '}
                        <a href={PATH_DEMO.SCHEDULE_DEMO} target={'_blank'} rel="noreferrer">
                          fill out this form →
                        </a>{' '}
                        It'll help us customize your demo preview.
                      </p>
                    ) : (
                      <p>
                        Great, you're set!
                        <br /> Thanks for opting to explore our demo – look out for our follow-up.
                        For a tailored experience, please{' '}
                        <a href={PATH_DEMO.SCHEDULE_DEMO} target={'_blank'} rel="noreferrer">
                          fill out this form →
                        </a>{' '}
                        It'll help us customize your demo preview.
                      </p>
                    )}
                  </div>
                  <form
                    id={'solveTicketsForm'}
                    onSubmit={submitForm}
                    className={clsx(
                      style.form,
                      'w-full max-md:flex-col',
                      requested ? style.done : ''
                    )}
                  >
                    {submitting && <RequestDemoSpinner />}
                    <div
                      className={clsx(
                        style.inputBorder,
                        validation.type ? style[validation.type] : '',
                        'flex-1'
                      )}
                    >
                      <input
                        ref={inputRef}
                        type={'email'}
                        className={clsx(style.input, 'h-[calc(100%-2px)]')}
                        value={email}
                        onChange={(e) => setEmail(e.currentTarget.value)}
                        onBlur={validate}
                        onFocus={(set) => setValidation(initialValidation)}
                        placeholder={'Your Business Email'}
                      />

                      <div className={style.validationPopup}>
                        {validation.type === 'incorrect' && (
                          <p>
                            <i>
                              <b>Look closely.</b>
                            </i>{' '}
                            You made a mistake in the email.
                          </p>
                        )}
                        {validation.type === 'corporate' && (
                          <p>
                            <i>
                              <b>You have written a private email.</b>
                            </i>{' '}
                            {!isMobile && 'Please enter your business email.'}
                          </p>
                        )}
                      </div>
                    </div>
                    <GreenButton
                      className={clsx(style.button, 'flex-1 whitespace-nowrap')}
                      disabled={submitting}
                      form={'solveTicketsForm'}
                    >
                      <Typography as="span" $color={COLORS.EBONY} $type="Heading4" $weight="700">
                        Get Started Now
                      </Typography>
                    </GreenButton>
                  </form>
                  <div className={clsx(style.ps, 'max-lg:hidden flex')}>
                    <PatentSolution />
                  </div>
                </div>
                <SwiperFreshdeskMain
                  className={'lg:max-w-full md:max-w-[70%] w-full flex flex-col justify-center'}
                />
                <div className={'flex items-center lg:hidden flex-wrap md:gap-5-vw gap-4'}>
                  <AwardsComponent />
                  <PatentSolution />
                </div>
              </div>
            </Container>
          </ContentContainer>
        </div>
        <MediaRecognition isDark={false} className="!bg-transparent lg:block hidden" />
      </SectionContainer>
    </>
  );
};
